import React, { useRef } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import AddToDrive from '@mui/icons-material/AddToDrive';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import PhoneIphoneOutlined from '@mui/icons-material/PhoneIphoneOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

interface SharedActionAccordionProps {
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  type?: string;
  expanded: boolean;
  onToggleExpand: (isExpanded: boolean) => void;
  onRemove?: () => void;
  children?: React.ReactNode;
  hasNeedsAttention?: boolean;
}

export default function SharedActionAccordion({
  id,
  title,
  description,
  icon,
  type,
  expanded,
  onToggleExpand,
  onRemove,
  children,
  hasNeedsAttention,
}: SharedActionAccordionProps) {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  const iconStyle = {
    width: 20,
    height: 20,
    color: theme.palette.primary.main,
  };

  const getActionIcon = (actionType: string): React.ReactNode => {
    switch (actionType) {
      case 'External':
      case 'Gmail Settings':
        return <EmailOutlined sx={iconStyle} />;
      case 'Policy':
        return <DescriptionOutlined sx={iconStyle} />;
      case 'User':
        return <PersonOutlined sx={iconStyle} />;
      case 'Groups':
        return <GroupsOutlined sx={iconStyle} />;
      case 'Calendar':
      case 'Resource Calendar':
        return <CalendarTodayOutlined sx={iconStyle} />;
      case 'Drive':
        return <AddToDrive sx={iconStyle} />;
      case 'Devices':
        return <PhoneIphoneOutlined sx={iconStyle} />;
      case 'Roles':
        return <AdminPanelSettingsOutlined sx={iconStyle} />;
      default:
        return <PersonOutlined sx={iconStyle} />;
    }
  };

  return (
    <Accordion
      id={id}
      ref={ref}
      expanded={expanded}
      onChange={(_, isExpanded) => onToggleExpand(isExpanded)}
      disableGutters
      sx={{
        mb: 2,
        borderRadius: 1,
        overflow: 'hidden',
        boxShadow: `0px 1px 1px 0px rgba(0,0,0,0.14),
                    0px 2px 1px -1px rgba(0,0,0,0.2),
                    0px 1px 3px 0px rgba(0,0,0,0.12)`,
        bgcolor: theme.palette.semantic.surface.bg1,
        border: '0.5px solid',
        borderColor: theme.palette.semantic.outline.light,
        '&:before': { display: 'none' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          padding: 1,
          userSelect: 'none',
          minHeight: 64,
          gap: 1,
          cursor: children ? 'pointer' : 'default',
        }}
        onClick={() => onToggleExpand(!expanded)}
      >
        {icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}

        {type ? (
          <Box
            sx={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {getActionIcon(type)}
          </Box>
        ) : null}

        <Box flex={1}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: theme.typography.body1.fontSize,
              color: theme.palette.text.primary,
            }}
          >
            {title}
            {hasNeedsAttention && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.warning.main,
                  ml: 2,
                  display: 'inline-flex',
                  alignItems: 'center',
                  verticalAlign: 'middle',
                }}
              >
                <WarningAmberOutlined
                  sx={{
                    width: 16,
                    height: 16,
                    mr: 0.5,
                  }}
                />
                Needs Attention
              </Typography>
            )}
          </Typography>
          {description && (
            <Typography
              sx={{
                fontSize: theme.typography.body2.fontSize,
                color: theme.palette.text.secondary,
                mt: 0.25,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center">
          {children ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(!expanded);
              }}
            >
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: '0.2s',
                }}
              />
            </IconButton>
          ) : null}

          {onRemove && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <AccordionSummary
        expandIcon={null}
        sx={{
          height: 0,
          minHeight: 0,
          padding: 0,
          margin: 0,
          visibility: 'hidden',
        }}
      />
      {children ? (
        <AccordionDetails sx={{ pt: 0, px: 5 }}>{children}</AccordionDetails>
      ) : null}
    </Accordion>
  );
}
